<template>
    <div class="card relative overflow-hidden"  
         @mouseenter="onMouseEnter" 
         @mouseleave="onMouseLeave">
        <Galleria
            ref="galleria"
            v-model:activeIndex="activeIndex"
            :value="formattedImages"
            :numVisible="5"
            containerStyle="max-width: 640px overflow-hidden"
            :showThumbnails="showThumbnails"
            :showItemNavigators="true"
            :showItemNavigatorsOnHover="true"
            :circular="true"
            :autoPlay="isAutoPlay"
            :showIndicators="true"
            :transitionInterval="4000"
            :responsiveOptions="responsiveOptions"
            :pt="{
                root: { class: [{ 'flex flex-col': fullScreen }] },
                content: { class: ['relative', { 'flex-1 justify-center': fullScreen }] },
                thumbnails: 'absolute w-full left-0 bottom-0',
                footer: { 
                    class: [
                        'absolute bottom-0 w-full transition-all duration-300 rounded-none',
                        {'opacity-0': !isHovering && !fullScreen, 'opacity-100': isHovering || fullScreen}
                    ] 
                },
                thumbnailContainer: { class: 'rounded-none' }
            }"
        >
            <template #item="slotProps">
                <img 
                    :src="slotProps.item.itemImageSrc" 
                    :alt="fixedAlt"  
                    @click="toggleFullScreen"
                    class="max-w-full overflow-hidden cursor-zoom-in w-full"
                    :style="{ maxHeight: '100%', objectFit: 'contain' }"
                />
            </template>
            <template #thumbnail="slotProps">
                <div class="grid gap-4 justify-center">
                    <img 
                        :src="slotProps.item.thumbnailImageSrc" 
                        :alt="fixedAlt"
                        class="w-16 h-16 object-cover object-center rounded-none"
                    />
                </div>
            </template>
            <template #footer>
                <div class="flex items-stretch bg-surface-950 text-white h-10" v-if="fullScreen">
                    <button 
                        type="button" 
                        @click="onThumbnailButtonClick" 
                        class="bg-transparent border-none rounded-none hover:bg-white/10 text-white inline-flex justify-center items-center cursor-pointer px-3"
                    >
                        <i class="pi pi-th-large"></i>
                    </button>
                    <button 
                        type="button" 
                        @click="toggleAutoSlide" 
                        class="bg-transparent border-none rounded-none hover:bg-white/10 text-white inline-flex justify-center items-center cursor-pointer px-3"
                    >
                        <i :class="slideButtonIcon"></i>
                    </button>
                    <span class="flex items-center gap-4 ml-3">
                        <span class="md:text-sm text-[12px]">{{ activeIndex + 1 }}/{{ formattedImages.length }}</span>
                        <span class="font-bold md:text-sm text-[12px]">{{ fixedAlt }}</span>  <!-- Alt fijo aquí -->
                    </span>
                    <button 
                        type="button" 
                        @click="toggleFullScreen" 
                        class="bg-transparent border-none rounded-none hover:bg-white/10 text-white inline-flex justify-center items-center cursor-pointer px-3 ml-auto"
                    >
                        <i :class="fullScreenIcon"></i>
                    </button>
                </div>
            </template>
            <template #indicator="slotProps">
                <div 
                    class="custom-indicator cursor-pointer" 
                    :class="{ 'active': slotProps.index === activeIndex }"
                    @click="onIndicatorClick(slotProps.index)"
                    v-if="!fullScreen"
                ></div>
            </template>
        </Galleria>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, defineProps } from "vue";
import Galleria from 'primevue/galleria';

interface GalleryItem {
    src: string;
    title: string;
}

const props = defineProps<{
    items: GalleryItem[];
}>();

const onIndicatorClick = (index: number) => {
    activeIndex.value = index;
};
// Usamos el alt del primer elemento como texto fijo
const fixedAlt = computed(() => {
    return props.items[0]?.title || '';
});

const formattedImages = computed(() => {
    return props.items.map((item: { src: any; title: any; }) => ({
        itemImageSrc: item.src,
        thumbnailImageSrc: item.src,
        alt: fixedAlt.value,  // Usamos el alt fijo para todas las imágenes
        title: fixedAlt.value
    }));
});

// Lógica de la galería (mantenida igual)
const galleria = ref();
const activeIndex = ref(0);
const showThumbnails = ref(false);
const fullScreen = ref(false);
const isAutoPlay = ref(true);
const isHovering = ref(false);

const onMouseEnter = () => isHovering.value = true;
const onMouseLeave = () => isHovering.value = false;
const toggleAutoSlide = () => isAutoPlay.value = !isAutoPlay.value;
const onThumbnailButtonClick = () => showThumbnails.value = !showThumbnails.value;

const responsiveOptions = ref([
    {
        breakpoint: '1300px',
        numVisible: 4
    },
    {
        breakpoint: '768px',
        numVisible: 3
    },
    {
        breakpoint: '575px',
        numVisible: 1
    }
]);

// Funciones para pantalla completa (mantenidas igual)
const toggleFullScreen = () => fullScreen.value ? closeFullScreen() : openFullScreen();
const onFullScreenChange = () => fullScreen.value = !fullScreen.value;

const openFullScreen = () => {
    let elem = galleria.value.$el;
    if (elem.requestFullscreen) elem.requestFullscreen();
    else if (elem.mozRequestFullScreen) elem.mozRequestFullScreen();
    else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
    else if (elem.msRequestFullscreen) elem.msRequestFullscreen();
};

const closeFullScreen = () => {
    if (document.exitFullscreen) document.exitFullscreen();
     // @ts-ignore
    else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
     // @ts-ignore
    else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
     // @ts-ignore
    else if (document.msExitFullscreen) document.msExitFullscreen();
};

const bindDocumentListeners = () => {
    document.addEventListener('fullscreenchange', onFullScreenChange);
    document.addEventListener('mozfullscreenchange', onFullScreenChange);
    document.addEventListener('webkitfullscreenchange', onFullScreenChange);
    document.addEventListener('msfullscreenchange', onFullScreenChange);
};

const unbindDocumentListeners = () => {
    document.removeEventListener('fullscreenchange', onFullScreenChange);
    document.removeEventListener('mozfullscreenchange', onFullScreenChange);
    document.removeEventListener('webkitfullscreenchange', onFullScreenChange);
    document.removeEventListener('msfullscreenchange', onFullScreenChange);
};

// Computed properties (mantenidas igual)
const fullScreenIcon = computed(() => `pi ${fullScreen.value ? 'pi-window-minimize' : 'pi-window-maximize'}`);
const slideButtonIcon = computed(() => `pi ${isAutoPlay.value ? 'pi-pause' : 'pi-play'}`);

onMounted(bindDocumentListeners);
onUnmounted(unbindDocumentListeners);
</script>

<style scoped>
/* Estilos originales mantenidos igual */
.card {
    max-width: 1200px;
    width: 100%;
    padding: 0;
}

.custom-indicator {
width: 8px;
height: 8px;
border-radius: 50%;
background-color: #ccc;
margin: 0 4px;
transition: all 0.3s ease;
}

.custom-indicator.active {
background-color: black;
transform: scale(1.2);
}

.indicator-container {
display: flex;
flex-direction: column;
align-items: center;
justify-content: center;
padding: 8px;
}

:deep(.p-galleria-item-nav) {
    background: rgba(59, 121, 59, 0.7) !important;
    color: white !important;
    border-radius: 50%;
    width: 2.5rem;
    height: 2.5rem;
    transition: all 0.3s ease;
}

:deep(.p-galleria-item-nav:hover) {
    background: rgba(39, 101, 39, 0.9) !important;
    transform: scale(1.1);
}

:deep(.p-galleria-item-nav i) {
    color: gray !important;
    font-size: 1.2rem;
}

:deep(.p-galleria-footer) {
    background: #999999 !important;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

:deep(.p-galleria-footer button) {
    color: #ecf0f1 !important;
    transition: all 0.3s ease;
}

:deep(.p-galleria-footer button:hover) {
    background: rgba(255, 255, 255, 0.1) !important;
    color: #ecf0f1 !important;
}

:deep(.p-galleria-footer span) {
    color: #ecf0f1 !important;
}

:deep(.p-galleria-thumbnail-container) {
    background: rgba(44, 62, 80, 0.9) !important;
    padding: 1rem;
}

:deep(.p-galleria-thumbnail-item) {
    border: 2px solid transparent;
    transition: all 0.3s ease;
}

:deep(.p-galleria-thumbnail-item:hover),
:deep(.p-galleria-thumbnail-item.p-highlight) {
    border-color: #2ecc71;
    opacity: 1;
    transform: scale(1.05);
}

:deep(.p-galleria-indicators .p-highlight button) {
    background: #2ecc71 !important;
}

:deep(.p-galleria),
:deep(.p-galleria-content),
:deep(.p-galleria-item),
:deep(.p-galleria-item-wrapper),
:deep(.p-galleria-item-container),
:deep(.p-galleria-thumbnail-container),
:deep(.p-galleria-thumbnail-item),
:deep(.p-galleria-thumbnail-items),
:deep(.p-galleria-footer) {
    border: none !important;
    border-radius: 0 !important;
}

:deep(.p-galleria-footer) {
    transition: opacity 0.3s ease, transform 0.3s ease;
    transform: translateY(100%);
}

.card:hover :deep(.p-galleria-footer),
:deep(.p-galleria.p-galleria-fullscreen .p-galleria-footer) {
    opacity: 1;
    transform: translateY(0);
}

:deep(.p-galleria-thumbnail-item) {
    border: none !important;
}
</style>